import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Trash2, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { LOGO_URL } from "../../lib/brand";
import { COMPANY } from "../../lib/jobConstants";
import { money } from "../../lib/jobCalc";

export const AdminCreditNoteDetail = () => {
  const { id } = useParams();
  const [cn, setCn] = useState(null);
  const [cust, setCust] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await adminApi.getCreditNote(id);
        setCn(data);
        if (data.customer_id) {
          try { setCust(await adminApi.getCustomer(data.customer_id)); }
          catch { /* silent */ }
        }
      } catch { toast.error("Could not load credit note"); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const printDoc = () => {
    document.body.classList.add("printing-doc");
    setTimeout(() => { window.print(); setTimeout(() => document.body.classList.remove("printing-doc"), 500); }, 50);
  };

  const remove = async () => {
    if (!window.confirm("Delete this credit note?")) return;
    try { await adminApi.deleteCreditNote(id); toast.success("Deleted"); window.location.href = "/admin/credit-notes"; }
    catch { toast.error("Could not delete"); }
  };

  if (loading) return <div className="p-10 text-center text-[#3f4b52]"><Loader2 className="animate-spin inline-block mr-2" size={16} /> Loading…</div>;
  if (!cn) return null;

  return (
    <div data-testid="admin-credit-note-detail">
      <div className="no-print flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/credit-notes" className="inline-flex items-center gap-1 text-xs font-display uppercase tracking-widest text-[#3f4b52] hover:text-[#f28a16]">
            <ArrowLeft size={14} /> Back to credit notes
          </Link>
          <h1 className="mt-1 font-display font-black uppercase text-3xl text-[#111619]">Credit note <span className="text-[#f28a16]">{cn.number}</span></h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => adminApi.openDoc(adminApi.creditNotePdfUrl(cn.id))}
            type="button"
            data-testid="cn-download-pdf"
            className="bg-[#f28a16] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:brightness-110"
          >
            <FileText size={14} /> Download PDF
          </button>
          <button onClick={printDoc} data-testid="cn-print" className="border border-[#cfd7db] text-[#3f4b52] px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:bg-[#f5f6f6]">
            <Printer size={14} /> Print preview
          </button>
          <button onClick={remove} className="border border-[#dc2626] text-[#dc2626] px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:bg-[#dc2626]/10">
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>

      <article className="mt-6 bg-white border border-[#cfd7db] rounded-md p-8 md:p-10 max-w-4xl mx-auto" data-testid="cn-doc">
        <header className="flex items-start justify-between gap-6 pb-6 border-b-2 border-[#111619]">
          <img src={LOGO_URL} alt="" className="h-16 w-auto object-contain" />
          <div className="text-right">
            <h2 className="font-display font-black uppercase text-3xl text-[#111619] leading-none">Credit note</h2>
            <div className="text-xs text-[#3f4b52] mt-2">{COMPANY.name} · {COMPANY.tradingAs}</div>
            <div className="text-xs text-[#3f4b52]">{COMPANY.email} · {COMPANY.phone}</div>
          </div>
        </header>

        <section className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
          <div>
            <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest">Customer</div>
            {cust ? (
              <>
                <div className="font-display font-black uppercase text-lg mt-1"><span className="text-[#f28a16] mr-2">{cust.code}</span>{cust.name}</div>
                {cust.vat_number && <div className="text-[#3f4b52]">VAT No: {cust.vat_number}</div>}
                {cust.billing_address && <div className="text-[#3f4b52] whitespace-pre-line">{cust.billing_address}</div>}
              </>
            ) : (
              <div className="text-[#3f4b52]">Customer {cn.customer_code}</div>
            )}
          </div>
          <div className="sm:text-right text-[#3f4b52]">
            <div><span className="text-xs uppercase font-display tracking-widest">Number</span> · <b className="text-[#111619]">{cn.number}</b></div>
            <div><span className="text-xs uppercase font-display tracking-widest">Date</span> · <b className="text-[#111619]">{cn.date}</b></div>
            {cn.invoice_reference && <div><span className="text-xs uppercase font-display tracking-widest">Against invoice</span> · <b className="text-[#111619]">{cn.invoice_reference}</b></div>}
            {cn.reason && <div><span className="text-xs uppercase font-display tracking-widest">Reason</span> · <b className="text-[#111619]">{cn.reason}</b></div>}
          </div>
        </section>

        {cn.description && (
          <p className="mt-6 text-sm text-[#3f4b52] italic border-l-4 border-[#f28a16] pl-3">{cn.description}</p>
        )}

        <section className="mt-8">
          <table className="w-full text-sm border border-[#cfd7db]">
            <thead className="bg-[#111619] text-white uppercase tracking-widest text-xs">
              <tr>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-right px-3 py-2">Qty</th>
                <th className="text-right px-3 py-2">Unit price</th>
                <th className="text-right px-3 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {(cn.lines || []).map((l, i) => (
                <tr key={i} className="border-t border-[#eef2f4]">
                  <td className="px-3 py-2">{l.description || "—"}</td>
                  <td className="px-3 py-2 text-right">{l.qty}</td>
                  <td className="px-3 py-2 text-right">{money(l.unit_price)}</td>
                  <td className="px-3 py-2 text-right">{money((l.qty || 1) * (l.unit_price || 0))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#f5f6f6]">
              <tr>
                <td colSpan={3} className="px-3 py-2 text-right text-[#3f4b52] uppercase tracking-widest text-xs">Subtotal</td>
                <td className="px-3 py-2 text-right">{money(cn.subtotal)}</td>
              </tr>
              <tr>
                <td colSpan={3} className="px-3 py-2 text-right text-[#3f4b52] uppercase tracking-widest text-xs">
                  VAT {cn.vat_mode === "inclusive" ? "(incl.)" : cn.vat_mode === "none" ? "(none)" : `(${cn.vat_pct}%)`}
                </td>
                <td className="px-3 py-2 text-right">{money(cn.vat)}</td>
              </tr>
              <tr className="bg-[#111619] text-white">
                <td colSpan={3} className="px-3 py-2 text-right font-display uppercase tracking-widest">Total credit</td>
                <td className="px-3 py-2 text-right font-display font-black text-lg text-[#ffc438]">{money(cn.gross)}</td>
              </tr>
            </tfoot>
          </table>
        </section>

        <footer className="mt-8 text-xs text-[#3f4b52] pt-6 border-t border-[#cfd7db]">
          <div>Reference this credit note against outstanding invoices for the customer.</div>
          <div>E&amp;OE. Diesel &amp; Dust Group.</div>
        </footer>
      </article>
    </div>
  );
};

export default AdminCreditNoteDetail;
